import { Button, FontIcon, RentTimer } from '@intigral/shared';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { checkShowLockIcon, isSubscriptionLockedContent, showUpgradeOrOpenDetail } from '../../helpers/vod';
import { __ } from '../../translations';
import AddWatchlistButton from './AddWatchlistButton';
import LikeDislikeButton from './LikeDislikeButton';
import h, { getLearnActionConfig, isTVodProgram } from '../../helpers/common';
import { MovieCard } from '@intigral/ui';
import { ButtonTypes, PROVIDER } from '../../config/constants';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import playerService from '../../helpers/bitmovinPlayer';
import { trackCarouselItemDetailClick } from '../../helpers/trackCarouselItemDetailClick';
import useDetailOverlay from '../../hooks/useDetailOverlay';
import { Trailer } from '../../features/detail-overlay/components/elements';
import styles from '@intigral/shared/styles/components/carousel.module.sass';
import useTimeToExpire from '../../hooks/useTimeToExpire';
import { useReminderItem } from '../../hooks/useReminderItem';
import { useTvod } from '../../hooks/useTvod';
import { MoviePlayButton } from './MoviePlayButton';
import { isGuestUser } from '../../config/guestMode';
import { getUserInfo } from '../../utils/user';

/** Delay before starting the hover trailer so quick mouse-overs do not mount Bitmovin. */
const TRAILER_HOVER_DELAY_MS = 500;

const formatTvodPrice = (priceWithVat, currency, isRented) => {
    if (isRented) {
        return __('tvod_poster_rented');
    }
    return `${priceWithVat} ${currency}`;
};

interface FullVODCarouselItemTypes {
    item?: any;
    index?: number;
    componentType?: string;
    componentName?: string;
    screenName?: string;
    carouselType?: string;
    autoTrailerCarouselConfiguration: {
        is_enabled: boolean;
        portait_image_width: number;
        portait_image_height: number;
        portait_image_quality: number;
        landscape_image_width: number;
        landscape_image_height: number;
    };
    promotionProviderImages?: any;
    enableLockIcon?: boolean;
    reminderData?: any;
}

export const FullVODCarouselItem: React.FC<FullVODCarouselItemTypes> = ({
    item,
    index,
    componentName,
    componentType,
    screenName,
    carouselType,
    autoTrailerCarouselConfiguration,
    promotionProviderImages,
    enableLockIcon,
    reminderData,
}) => {
    const openDetailOverlay = useDetailOverlay();
    const dispatch = useDispatch();
    const { track = () => undefined } = useContext(MixpanelContext) || {};

    const [isMuted, setIsMuted] = useState(true);
    const [isActive, setIsActive] = useState(false);
    const trailerHoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { priceInfo, isTvod, expiryDate, isRented } = useTvod(item);
    const { priceWithVat = '', currency = '' } = priceInfo || {};
    const { hours, minutes, durationInMinutes } = useTimeToExpire(expiryDate);
    const { isComingSoonItem, isReminded } = useReminderItem(item, reminderData ?? []);
    const isLocked = checkShowLockIcon(item?.subscriptionGuids ?? []) && enableLockIcon;
    const locked = (!isTvod && isLocked) || item?.isLocked;
    const isSubscriptionLocked = isSubscriptionLockedContent(item, locked);
    const playerInstance = useRef(null);
    const isGuest = isGuestUser(getUserInfo());
    const showLikeDislike = !isGuest && !!getLearnActionConfig()?.isLikeDislikeEnable;

    const clearTrailerHoverTimer = useCallback(() => {
        if (trailerHoverTimerRef.current) {
            clearTimeout(trailerHoverTimerRef.current);
            trailerHoverTimerRef.current = null;
        }
    }, []);

    const handleSetIsActive = useCallback(
        (active: boolean) => {
            clearTrailerHoverTimer();
            if (active) {
                trailerHoverTimerRef.current = setTimeout(() => {
                    trailerHoverTimerRef.current = null;
                    setIsActive(true);
                }, TRAILER_HOVER_DELAY_MS);
                return;
            }
            setIsActive(false);
        },
        [clearTrailerHoverTimer]
    );

    useEffect(() => () => clearTrailerHoverTimer(), [clearTrailerHoverTimer]);

    let tvodRentNowBtnText = __('tvod_rent_now_button_details') || '';
    if (priceWithVat) {
        tvodRentNowBtnText = tvodRentNowBtnText.replace('{priceWithVat}', `<label  dir="ltr"> ${priceWithVat}`);
    } else {
        tvodRentNowBtnText = tvodRentNowBtnText.replace(' - {priceWithVat}', ``);
    }
    tvodRentNowBtnText = tvodRentNowBtnText.replace('{currency}', `${currency}</label>`);
    tvodRentNowBtnText = tvodRentNowBtnText.replace(/<label(.*?)>/g, '<span$1>').replace(/<\/label>/g, '</span>');

    const handleMute = () => {
        if (playerInstance.current) {
            if (isMuted) {
                playerInstance.current.unmute();
            } else {
                playerInstance.current.mute();
            }
        }

        setIsMuted(!isMuted);
    };

    const openVodDetailOverlay = () => {
        if (item?.useCaseId) {
            playerService.setUseCaseId(item.useCaseId);
        }
        openDetailOverlay(item);
    };

    const handleCardClick = () => {
        trackCarouselItemDetailClick({
            track,
            item,
            componentType,
            componentName,
            meta: {},
            screenName,
            carouselType,
        });
        showUpgradeOrOpenDetail({ item, locked, dispatch, openDetail: openVodDetailOverlay });
    };

    return (
        <MovieCard
            key={index}
            cover={h.getImagizerUrl(
                item?.image,
                autoTrailerCarouselConfiguration.portait_image_width,
                autoTrailerCarouselConfiguration.portait_image_height,
                autoTrailerCarouselConfiguration.portait_image_quality
            )}
            provider={[
                h.getImagizerUrl(
                    promotionProviderImages?.[item?.provider]?.imageUrl,
                    PROVIDER.WIDTH,
                    PROVIDER.HEIGHT,
                    PROVIDER.QUALITY
                ),
            ]}
            play={
                <MoviePlayButton
                    isTvod={isTvod}
                    isRented={isRented}
                    item={item}
                    componentName={componentName}
                    componentType={componentType}
                    meta={{}}
                    tvodRentText={tvodRentNowBtnText}
                    openDetailOverlay={openDetailOverlay}
                    hours={hours}
                    minutes={minutes}
                    locked={locked}
                    isComingSoonItem={isComingSoonItem}
                />
            }
            info={item && Array.isArray(item?.genre) ? item.genre.map((c) => __(c)) : []}
            heading={item?.text}
            price={isTvod && priceWithVat && formatTvodPrice(priceWithVat, currency, isRented)}
            timer={
                isTvod &&
                isRented && (
                    <RentTimer
                        timeCount={hours > 0 ? [hours, 'hour'] : [minutes, 'minute']}
                        timeType={
                            hours > 0
                                ? __('tvod_rent_expiration_hours')
                                : minutes > 0
                                ? __('tvod_rent_expiration_minutes')
                                : ''
                        }
                        labelRemaining={__('tvod_rent_remaining_time')}
                        {...(hours < 0 && minutes < 0 ? { expired: true } : {})}
                        expiredLabel={__('tvod_rent_expired')}
                        {...(durationInMinutes < Number(h.getTVodConfig().diffExpirationTimeInMinutes)
                            ? { almostEnded: true }
                            : {})}
                    />
                )
            }
            picture={
                isActive && item?.trailerMediaUrl && autoTrailerCarouselConfiguration.is_enabled ? (
                    <Trailer
                        source={item?.trailerMediaUrl}
                        isMuted={isMuted}
                        item={item}
                        playerReadyCallback={(player) => {
                            playerInstance.current = player;
                        }}
                    >
                        <Button
                            primary
                            data-test-id="mute-button"
                            className={styles['button-mute']}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleMute();
                            }}
                        >
                            <FontIcon name={isMuted ? 'volume-off' : 'volume-high'} size="smaller" md="smaller"></FontIcon>
                        </Button>
                    </Trailer>
                ) : (
                    <img
                        src={
                            h.getImagizerUrl(
                                item?.image_landscape,
                                autoTrailerCarouselConfiguration.landscape_image_width,
                                autoTrailerCarouselConfiguration.landscape_image_height
                            ) || null
                        }
                        alt=""
                    />
                )
            }
            setIsActive={handleSetIsActive}
            watchlistButton={
                <AddWatchlistButton
                    item={item}
                    type={ButtonTypes.VodeItem}
                    componentType={componentType}
                    componentName={componentName}
                />
            }
            endActions={
                showLikeDislike ? (
                    <LikeDislikeButton
                        itemGuid={item?.guid || ''}
                        item={item}
                        likeLabel=""
                        dislikeLabel=""
                        square
                    />
                ) : (
                    <></>
                )
            }
            page={{
                onClick: (e) => {
                    e.stopPropagation();
                    openVodDetailOverlay();
                },
            }}
            {...(isLocked && !isTVodProgram(item) && { icon: 'locked' })}
            onClick={handleCardClick}
            {...(locked ? { icon: 'locked' } : {})}
            {...(isComingSoonItem && isReminded ? { icon: 'remind' } : {})}
            guid={item?.guid}
        />
    );
};
